import type { MapperContext } from '@empathyco/x-adapter'
import type { FacetConfig, PlatformFacetType, PlatformFilter } from '@empathyco/x-adapter-platform'
import type { SimpleFilter } from '@empathyco/x-types'
import { createMutableSchema } from '@empathyco/x-adapter'
import { getFacetConfig } from '@empathyco/x-adapter-platform'

/**
 * Retrieves the facet configuration with editable properties for a given facet ID and type.
 *
 * @param facetId - The ID of the facet to retrieve the configuration for.
 * @param type - The type of the platform facet.
 * @returns The facet configuration with editable properties.
 */
export const getFacetConfigWithEditable = (
  facetId: string,
  type: PlatformFacetType,
): FacetConfig => {
  if (facetId === 'priceCurrentValue') {
    const salePriceConfig = getFacetConfig('editable-range')
    const getSalePriceFilterId = (context?: MapperContext): string | undefined =>
      (context?.requestParameters?.filter as string[])?.find(filter =>
        filter.includes('priceCurrentValue'),
      )

    salePriceConfig.schema = {
      ...salePriceConfig.schema,
      id: ({ filter }, $context): string => {
        const currentSalePriceFilterId = getSalePriceFilterId($context)
        return currentSalePriceFilterId ?? (filter as string)
      },
      label: (_, $context): string | undefined => getSalePriceFilterId($context)?.split(':')[1],
      range: ({ value }, $context): { min: number | null; max: number | null } => {
        const currentSalePriceFilterId = getSalePriceFilterId($context)
        const stringRange = currentSalePriceFilterId?.split(':')[1] ?? (value as string)

        const min = Number(stringRange.split('-')[0])
        const max = Number(stringRange.split('-')[1])

        return {
          min: Number.isNaN(min) ? null : min,
          max: Number.isNaN(max) ? null : max,
        }
      },
    }

    return salePriceConfig
  } else if (facetId === 'facetColorGroup') {
    return getColorConfig()
  }

  return getFacetConfig(type)
}

const colorFilterSchema = createMutableSchema<PlatformFilter, SimpleFilter>({
  facetId: (_, $context) => $context?.facetId as string,
  label: ({ value }) => splitAndTrimByPipe(value)[0] || '',
  colorHex: ({ value }) => splitAndTrimByPipe(value)[1] || '',
  id: 'filter',
  totalResults: 'count',
  selected: () => false,
  modelName: () => 'SimpleFilter',
  value: 'value',
})

function getColorConfig(): FacetConfig {
  return {
    modelName: 'SimpleFacet',
    schema: colorFilterSchema,
  }
}

/**
 * Splits a string by '|' and returns both parts trimmed.
 *
 * @param input - The input string to split.
 * @returns A tuple with both trimmed values, or [undefined, undefined] if not present.
 */
function splitAndTrimByPipe(input: string): [string | undefined, string | undefined] {
  const [first, second] = input.split('|')
  return [first?.trim(), second?.trim()]
}
